/**
 * RecurrencePicker - Google-Calendar-style recurrence configurator.
 *
 * Atomic Design role: Molecule. Composes:
 *   - The native <select> dropdown of presets
 *   - An inline (NOT modal) "Custom recurrence" panel revealed when the
 *     user picks "Custom..."
 *   - A live human-readable preview of the configured rule
 *   - A line saying when the first job appears
 *
 * Used by the SettingsSection molecule on the JobForm. State is fully
 * controlled: the parent owns the `mode` + `rule` + `startDate` values
 * and the molecule only emits change events. This keeps the form's
 * submit logic simple — the picker is a pure projection of state.
 *
 * Ends defaults to Never. Recurring jobs used to be capped at a year and
 * all created at save time; since Oct 2026 the server creates each job on
 * its date, so a schedule simply runs until someone stops it (beta
 * feedback: "Reoccurring work orders should not have an end date"). An
 * end date or count can still be set in the custom panel when one is
 * genuinely wanted.
 */
import React, { useEffect, useMemo } from 'react';
import {
	emptyRule,
	defaultEndDate,
	formatRecurrenceSummary,
	formatRecurrenceEnd,
	matchPresetId,
	presetOptions,
	WEEKDAY_LETTER,
	WEEKDAY_SHORT,
	type FrequencyUnit,
	type RecurrenceMode,
	type RecurrenceRule,
} from '../../../utils/recurrence';

/* ============================================
 * TYPES
 * ============================================ */

export interface RecurrencePickerProps {
	/** Whether recurrence is off, set to a preset, or set to a custom rule. */
	mode: RecurrenceMode;
	/** The configured rule. Read but not used when mode === 'none'. */
	rule: RecurrenceRule;
	/** The start-date anchor that drives the preset labels (YYYY-MM-DD). */
	startDate: string;
	/**
	 * Emits the new (mode, rule) pair on every user interaction. The
	 * parent re-renders the picker with the next values.
	 */
	onChange: (next: { mode: RecurrenceMode; rule: RecurrenceRule }) => void;
	/** Shared styling tokens — passed down from JobForm via SettingsSection. */
	labelClass: string;
	inputClass: string;
	normalBorder: string;
	/** Optional id used by the label htmlFor attribute. */
	id?: string;
	/**
	 * When true, treat the picker as disabled (e.g. start date not
	 * picked yet). Hides the inline custom panel and shows a hint.
	 */
	disabled?: boolean;
	/** Optional extra classes for the outer wrapper. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const RecurrencePicker: React.FC<RecurrencePickerProps> = ({
	mode,
	rule,
	startDate,
	onChange,
	labelClass,
	inputClass,
	normalBorder,
	id = 'recurrence',
	disabled = false,
	className = '',
}) => {
	/* Anchor date for preset label generation. Falls back to today so
	   the preview text remains coherent even before the user picks a
	   start date (the rule itself will refuse to save without one). */
	const anchorDate = useMemo(() => {
		return startDate ? new Date(`${startDate}T00:00:00`) : new Date();
	}, [startDate]);

	const presets = useMemo(() => presetOptions(anchorDate), [anchorDate]);

	/* The matched preset id, used to keep the dropdown selection in
	   sync after edit-mode hydration (where we receive a rule but no
	   explicit preset choice). */
	const detectedPresetId = useMemo(() => {
		if (mode !== 'preset') return null;
		return matchPresetId(rule, anchorDate);
	}, [mode, rule, anchorDate]);

	/* The string that drives the <select>'s value. We use a few
	   sentinel strings (`__none`, `__custom`) for the non-preset rows
	   so the value space stays simple. */
	const selectValue = (() => {
		if (mode === 'none') return '__none';
		if (mode === 'custom') return '__custom';
		return detectedPresetId ?? '__custom';
	})();

	/* Live preview text — the human-readable rule plus how jobs appear.
	   There's no "N occurrences" count any more: jobs aren't created up
	   front, so the honest thing to say is that each one shows up on its
	   date. */
	const occurrencePreview = useMemo(() => {
		if (mode === 'none') return null;
		const summary = formatRecurrenceSummary(rule);
		const end = formatRecurrenceEnd(rule);
		const tail = end ? ` ${end}` : ', no end date';
		return `${summary}${tail}. Each job is created on its date.`;
	}, [mode, rule]);

	/* Sync the rule's start_date to whatever the parent provided so the
	   preview + preset labels never drift out of sync with the form's
	   "Scheduled Date" / "Inspection Date" input. */
	useEffect(() => {
		if (!startDate || mode === 'none') return;
		if (rule.start_date === startDate) return;
		onChange({ mode, rule: { ...rule, start_date: startDate } });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startDate]);

	/* -------------------------------------------------------------------
	 * Dropdown change handler
	 * ----------------------------------------------------------------- */
	const handlePresetSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value;
		if (value === '__none') {
			onChange({ mode: 'none', rule });
			return;
		}
		if (value === '__custom') {
			/* When swapping into custom mode, seed the rule with a sane
			   default so the inline panel renders with meaningful fields
			   instead of an empty week / no weekdays. */
			const next: RecurrenceRule = mode === 'none' ? emptyRule(startDate) : { ...rule };
			onChange({ mode: 'custom', rule: next });
			return;
		}
		const preset = presets.find((p) => p.id === value);
		if (!preset) return;
		const built = preset.build(anchorDate);
		// Presets never end — the schedule runs until someone stops it.
		built.end_type = 'never';
		built.end_date = null;
		built.end_count = null;
		onChange({ mode: 'preset', rule: built });
	};

	/* -------------------------------------------------------------------
	 * Custom-panel field handlers — each one patches the rule and emits
	 * the new state up. Kept verbose so the call sites read like the
	 * form fields they're driving.
	 * ----------------------------------------------------------------- */
	const setRule = (patch: Partial<RecurrenceRule>) => {
		onChange({ mode: 'custom', rule: { ...rule, ...patch } });
	};

	const toggleWeekday = (dow: number) => {
		const present = (rule.by_weekday || []).includes(dow);
		const next = present
			? rule.by_weekday.filter((d) => d !== dow)
			: [...(rule.by_weekday || []), dow].sort((a, b) => a - b);
		setRule({ by_weekday: next });
	};

	/* -------------------------------------------------------------------
	 * Render
	 * ----------------------------------------------------------------- */

	return (
		<div className={`recurrence-picker max-w-md ${className}`} data-ref="molecule-recurrence-picker">
			<label htmlFor={id} className={labelClass}>Frequency (optional)</label>
			<select
				id={id}
				value={selectValue}
				onChange={handlePresetSelect}
				disabled={disabled}
				className={`${inputClass} ${normalBorder}`}
			>
				<option value="__none">Does not repeat</option>
				{presets.map((p) => (
					<option key={p.id} value={p.id}>{p.label}</option>
				))}
				<option value="__custom">Custom…</option>
			</select>
			<p className="mt-4px text-12px font-sans text-brand-text-muted">
				Set for recurring jobs. Leave blank for one-time work.
			</p>

			{/* Inline custom panel — embedded into the form, not a modal,
			    per the user spec. */}
			{mode === 'custom' && !disabled && (
				<div className="recurrence-picker__custom-panel mt-12px p-16px rounded-md border border-brand-border bg-brand-surface/40 space-y-16px">
					{/* --- Repeat every N [unit] --- */}
					<div className="flex items-center gap-8px flex-wrap">
						<span className="text-13px font-medium text-brand-text">Repeat every</span>
						<input
							type="number"
							min={1}
							max={99}
							value={rule.interval}
							onChange={(e) => setRule({ interval: Math.max(1, Number(e.target.value || 1)) })}
							className="w-64px px-8px py-4px rounded-md border border-gray-300 text-16px desktop:text-14px focus:outline-none focus:ring-2 focus:ring-brand-primary"
						/>
						<select
							value={rule.frequency_unit}
							onChange={(e) => setRule({ frequency_unit: e.target.value as FrequencyUnit, by_weekday: e.target.value === 'week' ? rule.by_weekday : [] })}
							className="px-8px py-4px rounded-md border border-gray-300 text-16px desktop:text-14px bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
						>
							<option value="day">{rule.interval === 1 ? 'day' : 'days'}</option>
							<option value="week">{rule.interval === 1 ? 'week' : 'weeks'}</option>
							<option value="month">{rule.interval === 1 ? 'month' : 'months'}</option>
							<option value="year">{rule.interval === 1 ? 'year' : 'years'}</option>
						</select>
					</div>

					{/* --- Repeat on (weekday chips, only when unit='week') --- */}
					{rule.frequency_unit === 'week' && (
						<div>
							<div className="text-13px font-medium text-brand-text mb-6px">Repeat on</div>
							<div className="flex items-center gap-6px flex-wrap">
								{WEEKDAY_LETTER.map((letter, idx) => {
									const selected = (rule.by_weekday || []).includes(idx);
									return (
										<button
											type="button"
											key={`${letter}-${idx}`}
											onClick={() => toggleWeekday(idx)}
											aria-label={WEEKDAY_SHORT[idx]}
											aria-pressed={selected}
											className={[
												'w-44px h-44px desktop:w-32px desktop:h-32px rounded-full text-13px font-semibold transition-colors flex items-center justify-center',
												selected
													? 'bg-brand-primary text-white border border-brand-primary'
													: 'bg-white text-brand-text-muted border border-gray-300 hover:border-brand-primary',
											].join(' ')}
										>
											{letter}
										</button>
									);
								})}
							</div>
						</div>
					)}

					{/* --- Ends terminator --- */}
					<div className="recurrence-picker__ends">
						<div className="text-13px font-medium text-brand-text mb-8px">Ends</div>
						{/* Each option row is a full 44px touch target (CONVENTIONS
						    §touch): the 14px radio alone is too small to hit on a
						    phone, so the whole labelled row is the target. */}
						<div className="space-y-2px">
							{/* Never — the default. Jobs are created as they fall
							    due, so an open-ended schedule costs nothing. */}
							<label className="recurrence-picker__end-option flex items-center gap-8px min-h-44px cursor-pointer">
								<input
									type="radio"
									name={`${id}-end-type`}
									value="never"
									checked={rule.end_type === 'never'}
									onChange={() => setRule({ end_type: 'never', end_date: null, end_count: null })}
									className="w-14px h-14px text-brand-primary"
								/>
								<span className="text-13px text-brand-text">Never</span>
							</label>
							{/* On <date> — optional; no upper limit. */}
							<label className="recurrence-picker__end-option flex items-center gap-8px min-h-44px cursor-pointer">
								<input
									type="radio"
									name={`${id}-end-type`}
									value="on"
									checked={rule.end_type === 'on'}
									onChange={() => setRule({ end_type: 'on', end_count: null, end_date: rule.end_date || defaultEndDate(rule.start_date) })}
									className="w-14px h-14px text-brand-primary"
								/>
								<span className="text-13px text-brand-text">On</span>
								<input
									type="date"
									value={rule.end_date || ''}
									min={rule.start_date || undefined}
									onChange={(e) => setRule({ end_type: 'on', end_date: e.target.value })}
									disabled={rule.end_type !== 'on'}
									className="px-8px py-4px rounded-md border border-gray-300 text-16px desktop:text-14px bg-white disabled:bg-gray-100 disabled:text-gray-400"
								/>
							</label>
							<label className="recurrence-picker__end-option flex items-center gap-8px min-h-44px cursor-pointer">
								<input
									type="radio"
									name={`${id}-end-type`}
									value="after"
									checked={rule.end_type === 'after'}
									onChange={() => setRule({ end_type: 'after', end_count: rule.end_count || 13, end_date: null })}
									className="w-14px h-14px text-brand-primary"
								/>
								<span className="text-13px text-brand-text">After</span>
								<input
									type="number"
									min={1}
									max={366}
									value={rule.end_count ?? ''}
									onChange={(e) => setRule({ end_type: 'after', end_count: Math.max(1, Number(e.target.value || 1)) })}
									disabled={rule.end_type !== 'after'}
									className="w-72px px-8px py-4px rounded-md border border-gray-300 text-16px desktop:text-14px bg-white disabled:bg-gray-100 disabled:text-gray-400"
								/>
								<span className="text-13px text-brand-text-muted">occurrences</span>
							</label>
						</div>
						<p className="mt-6px text-11px text-brand-text-muted">
							Leave it on Never unless the work genuinely stops. Each job is created on its date, so a long schedule doesn't fill the job list.
						</p>
					</div>
				</div>
			)}

			{/* Live preview — shown for both preset and custom modes. */}
			{occurrencePreview && (
				<p className="recurrence-picker__preview mt-8px text-12px font-sans text-brand-text" aria-live="polite">
					<span className="font-semibold">Preview:</span> {occurrencePreview}
				</p>
			)}
		</div>
	);
};

export default RecurrencePicker;
